// Molecule → therapeutic class map used for same-class duplicate detection.
// Match is done against the generic_name + composition + brand_name string of
// each Rx row, lower-cased. First-match wins.
//
// Keep patterns intentionally broad (substring / word boundary) so brand-name
// only rows still classify correctly. Order does not matter — a row is
// tagged with EVERY class it matches (e.g. an ARNI row hits both `arni` and
// implicitly conflicts with `acei` / `arb`).

const DRUG_CLASSES = [
  {
    key: "sglt2",
    label: "SGLT2 inhibitor",
    patterns: [/empagliflozin/i, /dapagliflozin/i, /canagliflozin/i, /ertugliflozin/i],
  },
  {
    key: "glp1",
    label: "GLP-1 receptor agonist",
    patterns: [/semaglutide/i, /liraglutide/i, /dulaglutide/i, /exenatide/i, /lixisenatide/i],
  },
  {
    key: "gip_glp1",
    label: "GIP/GLP-1 dual agonist",
    patterns: [/tirzepatide/i, /retatrutide/i],
  },
  {
    key: "dpp4",
    label: "DPP-4 inhibitor (gliptin)",
    patterns: [/sitagliptin/i, /linagliptin/i, /vildagliptin/i, /teneligliptin/i, /saxagliptin/i, /alogliptin/i, /gemigliptin/i],
  },
  {
    key: "sulfonylurea",
    label: "Sulfonylurea",
    patterns: [/glimepiride/i, /gliclazide/i, /glipizide/i, /glibenclamide/i, /glyburide/i, /gliquidone/i],
  },
  {
    key: "biguanide",
    label: "Biguanide (Metformin)",
    patterns: [/\bmetformin\b/i],
  },
  {
    key: "tzd",
    label: "Thiazolidinedione (TZD)",
    patterns: [/pioglitazone/i, /rosiglitazone/i],
  },
  {
    key: "alpha_glu",
    label: "α-glucosidase inhibitor",
    patterns: [/acarbose/i, /voglibose/i, /miglitol/i],
  },
  {
    key: "insulin",
    label: "Insulin",
    patterns: [/\binsulin\b/i, /glargine/i, /degludec/i, /detemir/i, /lispro/i, /aspart/i, /glulisine/i, /\bnph\b/i, /70\/30/i, /50\/50/i, /premix/i],
  },
  {
    key: "statin",
    label: "Statin",
    patterns: [/rosuvastatin/i, /atorvastatin/i, /simvastatin/i, /pravastatin/i, /pitavastatin/i, /lovastatin/i, /fluvastatin/i],
  },
  {
    key: "ezetimibe",
    label: "Ezetimibe",
    patterns: [/ezetimibe/i],
  },
  {
    key: "fibrate",
    label: "Fibrate",
    patterns: [/fenofibrate/i, /gemfibrozil/i, /bezafibrate/i],
  },
  {
    key: "pcsk9",
    label: "PCSK9 inhibitor",
    patterns: [/evolocumab/i, /alirocumab/i, /inclisiran/i],
  },
  {
    key: "acei",
    label: "ACE inhibitor",
    patterns: [/ramipril/i, /enalapril/i, /lisinopril/i, /perindopril/i, /captopril/i, /benazepril/i, /fosinopril/i, /quinapril/i, /trandolapril/i],
  },
  {
    key: "arb",
    label: "ARB",
    // NOTE: `valsartan` alone is an ARB, but Sacubitril+Valsartan (ARNI) is
    // handled separately below and NEVER also gets tagged as `arb`.
    patterns: [/telmisartan/i, /losartan/i, /olmesartan/i, /\bvalsartan\b/i, /irbesartan/i, /candesartan/i, /azilsartan/i, /eprosartan/i],
  },
  {
    key: "arni",
    label: "ARNI (Sacubitril + Valsartan)",
    patterns: [/sacubitril/i, /arni/i],
    // ARNI is exclusive — never tag it as ARB even though it contains valsartan.
    override: (text) => /sacubitril/i.test(text),
  },
  {
    key: "ccb_dhp",
    label: "Calcium channel blocker (dihydropyridine)",
    patterns: [/amlodipine/i, /nifedipine/i, /felodipine/i, /nicardipine/i, /lercanidipine/i, /cilnidipine/i, /benidipine/i],
  },
  {
    key: "ccb_non_dhp",
    label: "Calcium channel blocker (non-DHP)",
    patterns: [/diltiazem/i, /verapamil/i],
  },
  {
    key: "beta_blocker",
    label: "Beta-blocker",
    patterns: [/bisoprolol/i, /carvedilol/i, /metoprolol/i, /nebivolol/i, /atenolol/i, /propranolol/i, /labetalol/i, /esmolol/i],
  },
  {
    key: "thiazide",
    label: "Thiazide / thiazide-like diuretic",
    patterns: [/hydrochlorothiazide/i, /\bhctz\b/i, /chlorthalidone/i, /chlortalidone/i, /indapamide/i, /metolazone/i, /bendroflumethiazide/i],
  },
  {
    key: "loop",
    label: "Loop diuretic",
    patterns: [/furosemide/i, /torsemide/i, /torasemide/i, /bumetanide/i, /ethacrynic/i],
  },
  {
    key: "mra",
    label: "Mineralocorticoid receptor antagonist (MRA)",
    patterns: [/spironolactone/i, /eplerenone/i, /finerenone/i],
  },
  {
    key: "alpha_blocker",
    label: "Alpha-blocker",
    patterns: [/prazosin/i, /terazosin/i, /doxazosin/i, /tamsulosin/i, /alfuzosin/i],
  },
  {
    key: "aspirin",
    label: "Aspirin-containing antiplatelet",
    // Detects free aspirin OR aspirin-containing FDCs (Ecosprin AV, Rosave A).
    patterns: [/\baspirin\b/i, /acetylsalicylic/i, /ecosprin/i, /\bloprin\b/i],
  },
  {
    key: "adp_antiplatelet",
    label: "ADP receptor antagonist",
    patterns: [/clopidogrel/i, /prasugrel/i, /ticagrelor/i, /cangrelor/i],
  },
  {
    key: "anticoagulant_doac",
    label: "Direct oral anticoagulant (DOAC)",
    patterns: [/apixaban/i, /rivaroxaban/i, /dabigatran/i, /edoxaban/i],
  },
  {
    key: "vka",
    label: "Vitamin-K antagonist",
    patterns: [/warfarin/i, /acenocoumarol/i],
  },
  {
    key: "ppi",
    label: "Proton pump inhibitor",
    patterns: [/pantoprazole/i, /omeprazole/i, /rabeprazole/i, /esomeprazole/i, /lansoprazole/i, /dexlansoprazole/i, /dexrabeprazole/i, /ilaprazole/i],
  },
  {
    key: "h2",
    label: "H2 receptor blocker",
    patterns: [/ranitidine/i, /famotidine/i, /cimetidine/i, /nizatidine/i],
  },
  {
    key: "thyroid",
    label: "Thyroid hormone",
    patterns: [/levothyroxine/i, /l-thyroxine/i, /liothyronine/i],
  },
  {
    key: "vitd",
    label: "Vitamin D / analogue",
    patterns: [/cholecalciferol/i, /ergocalciferol/i, /calcitriol/i, /alfacalcidol/i, /paricalcitol/i, /vitamin d3/i, /vit\s*d3/i],
  },
  {
    key: "nsaid",
    label: "NSAID",
    patterns: [/diclofenac/i, /aceclofenac/i, /ibuprofen/i, /naproxen/i, /etoricoxib/i, /celecoxib/i, /ketorolac/i, /mefenamic/i, /piroxicam/i, /indomethacin/i, /nimesulide/i],
  },
  {
    key: "antihistamine",
    label: "Antihistamine (H1)",
    patterns: [/bilastine/i, /fexofenadine/i, /levocetirizine/i, /cetirizine/i, /loratadine/i, /desloratadine/i, /chlorpheniramine/i, /hydroxyzine/i, /rupatadine/i, /ebastine/i],
  },
  {
    key: "benzo",
    label: "Benzodiazepine / Z-drug",
    patterns: [/alprazolam/i, /diazepam/i, /lorazepam/i, /clonazepam/i, /nitrazepam/i, /zolpidem/i, /etizolam/i],
  },
  {
    key: "ssri_snri",
    label: "SSRI / SNRI",
    patterns: [/escitalopram/i, /sertraline/i, /fluoxetine/i, /paroxetine/i, /fluvoxamine/i, /desvenlafaxine/i, /venlafaxine/i, /duloxetine/i],
  },
  {
    key: "opioid",
    label: "Opioid analgesic",
    patterns: [/tramadol/i, /tapentadol/i, /\bcodeine\b/i, /morphine/i, /oxycodone/i, /fentanyl/i],
  },
  {
    key: "paracetamol",
    label: "Paracetamol-containing",
    patterns: [/paracetamol/i, /acetaminophen/i],
  },
  {
    key: "pde5",
    label: "PDE5 inhibitor",
    patterns: [/sildenafil/i, /tadalafil/i, /vardenafil/i, /avanafil/i],
  },
  {
    key: "nitrate",
    label: "Nitrate",
    patterns: [/isosorbide/i, /nitroglycerin/i, /glyceryl trinitrate/i, /\bgtn\b/i, /nicorandil/i],
  },
];

// Cross-class MUTUAL exclusions — combinations that are contraindicated by
// guideline even though the two rows are technically different classes.
// Handled separately from the "2× same class" detection.
const CROSS_CONFLICTS = [
  {
    left: "pde5", right: "nitrate",
    reason: "PDE5 inhibitor + nitrate — profound refractory hypotension. ABSOLUTE contraindication.",
    level: "critical",
  },
  {
    left: "nsaid", right: "anticoagulant_doac",
    reason: "NSAID + DOAC — major GI bleeding risk. Use paracetamol/topical NSAID instead.",
    level: "major",
  },
  {
    left: "nsaid", right: "vka",
    reason: "NSAID + warfarin/acenocoumarol — major bleeding risk and INR destabilisation.",
    level: "major",
  },
  {
    left: "opioid", right: "benzo",
    reason: "Opioid + benzodiazepine/Z-drug — additive respiratory depression and sedation (falls). Avoid or use lowest doses briefly.",
    level: "major",
  },
  {
    left: "acei", right: "arb",
    reason: "ACE-inhibitor + ARB dual RAAS blockade — ONTARGET (2008) showed harm (renal failure, hyperkalaemia, hypotension). Use one, not both.",
    level: "critical",
  },
  {
    left: "acei", right: "arni",
    reason: "ARNI + ACE-inhibitor is contraindicated (angioedema risk). Require ≥ 36-hour washout before switching from ACEi to ARNI.",
    level: "critical",
  },
  {
    left: "arb", right: "arni",
    reason: "ARNI contains valsartan — do NOT combine with another ARB. Deprescribe the standalone ARB.",
    level: "critical",
  },
  {
    left: "dpp4", right: "glp1",
    reason: "DPP-4 inhibitor + GLP-1 receptor agonist share pathway; combining offers no additional benefit and doubles cost.",
    level: "major",
  },
  {
    left: "dpp4", right: "gip_glp1",
    reason: "DPP-4 inhibitor + GIP/GLP-1 dual agonist share the incretin pathway — no additional benefit, doubles cost.",
    level: "major",
  },
  {
    left: "glp1", right: "gip_glp1",
    reason: "GLP-1 RA + GIP/GLP-1 dual agonist should not be combined (redundant GLP-1 activity, cost, GI side-effects).",
    level: "critical",
  },
  {
    left: "thiazide", right: "loop",
    reason: "Combined thiazide + loop diuretic (sequential nephron blockade) is potent — verify need, monitor K+ / Na+ closely.",
    level: "caution",
  },
  {
    left: "ccb_non_dhp", right: "beta_blocker",
    reason: "Verapamil/Diltiazem + β-blocker → risk of severe bradycardia and AV block. Use only under specialist supervision.",
    level: "critical",
  },
  {
    left: "aspirin", right: "adp_antiplatelet",
    reason: "Dual antiplatelet therapy (DAPT) — intended only post-ACS/PCI. Verify indication + duration; add PPI cover.",
    level: "caution",
  },
  {
    left: "anticoagulant_doac", right: "vka",
    reason: "DOAC + Warfarin is never indicated — massive bleeding risk. Deprescribe one.",
    level: "critical",
  },
  {
    left: "anticoagulant_doac", right: "aspirin",
    reason: "Anticoagulant + aspirin roughly TRIPLES major bleeding risk. Continue only if strong indication (e.g. recent PCI); add PPI.",
    level: "caution",
  },
];

// ---------- API ----------

// Return the array of class keys a single Rx row matches (may be empty).
export function classifyRow(row) {
  if (!row) return [];
  const text = [row.generic_name, row.composition, row.brand_name].filter(Boolean).join(" ").toLowerCase();
  if (!text) return [];
  const hits = new Set();
  for (const c of DRUG_CLASSES) {
    if (c.override && c.override(text)) {
      hits.add(c.key);
      continue;
    }
    if (c.patterns.some((rx) => rx.test(text))) hits.add(c.key);
  }
  // ARNI override — if row is ARNI, remove the ARB tag (valsartan collides).
  if (hits.has("arni")) hits.delete("arb");
  return Array.from(hits);
}

// Build the therapeutic-class alerts for the current medicines list.
// Returns array of { level, msg, rows: [indices] } — merged with the drug-
// safety alerts so the existing critical-override flow gates the Save button.
export function sameClassAlerts(medicines) {
  const alerts = [];
  const classToRows = new Map(); // class key -> [{ idx, row }]
  medicines.forEach((row, idx) => {
    if (!row?.brand_name && !row?.generic_name && !row?.composition) return;
    for (const cls of classifyRow(row)) {
      if (!classToRows.has(cls)) classToRows.set(cls, []);
      classToRows.get(cls).push({ idx, row });
    }
  });

  // 1) Duplicates within the same class (2× SGLT2, 2× statin, etc.)
  for (const [cls, entries] of classToRows.entries()) {
    if (entries.length < 2) continue;
    // Special case: insulin can legitimately be prescribed as basal + prandial.
    // Only flag when generic names actually match (dedupe already covers that).
    if (cls === "insulin") continue;
    const label = DRUG_CLASSES.find((c) => c.key === cls)?.label || cls;
    const rowNumbers = entries.map((e) => e.idx + 1).join(" & ");
    const drugs = entries.map((e) => e.row.brand_name || e.row.generic_name || e.row.composition).filter(Boolean);
    alerts.push({
      level: cls === "aspirin" ? "critical" : "major",
      msg: `Two ${label}s prescribed (rows ${rowNumbers}: ${drugs.join(" + ")}). Same-class duplication should be avoided — deprescribe one.`,
      rows: entries.map((e) => e.idx),
      classKey: cls,
    });
  }

  // 2) Cross-class conflicts (ACEi + ARB, etc.)
  for (const conflict of CROSS_CONFLICTS) {
    const leftRows = classToRows.get(conflict.left);
    const rightRows = classToRows.get(conflict.right);
    if (!leftRows?.length || !rightRows?.length) continue;
    const rowNumbers = [...leftRows, ...rightRows].map((e) => e.idx + 1).sort((a, b) => a - b).join(" & ");
    alerts.push({
      level: conflict.level,
      msg: `${conflict.reason} (rows ${rowNumbers})`,
      rows: [...leftRows, ...rightRows].map((e) => e.idx),
      conflict: `${conflict.left}+${conflict.right}`,
    });
  }

  return alerts;
}
